// ============================================================================
// CORRESPONDENCE EMAIL SETTINGS PANEL — shared outbound-routing settings.
// Extracted from admin-hub CorrespondenceSettings so the same external
// connector UX can be embedded elsewhere (e.g. the Communications work-item
// type configuration modal) without sending clients to Total-Connect.
//
// Usage:
//   const ref = useRef<CorrespondenceEmailSettingsPanelHandle>(null);
//   <CorrespondenceEmailSettingsPanel ref={ref} embedded onSaved={...} />
//   ref.current?.save()   // from an external Save button
// ============================================================================

'use client';

/**
 * USER DOCS: changes to this component should be accompanied by a
 * corresponding update to the user guide:
 * next_portal/user-docs/content/docs/total-operations/workflows/email-task-management/sources-automations.mdx
 * (published at user-docs.totalaccess.co.za/total-operations/workflows/email-task-management/sources-automations#correspondence-connector)
 */

import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import {
  AlertCircle,
  Check,
  Loader2,
  Mail,
  Plus,
  Server,
  X,
} from 'lucide-react';
import { authService } from '@/lib/authService';
import { notify } from '@/lib/notifications';
import UserDocsLink from '@/components/shared/UserDocsLink';

interface CorrespondenceFormData {
  correspondence_use_external_connector: boolean;
  correspondence_external_connector_id: string;
  correspondence_external_from_email: string;
  correspondence_external_reply_to_email: string;
}

interface EmailConnector {
  id: number;
  name: string;
  connector_type?: string;
  smart_host: string;
  auth_username: string;
  rewrite_from?: string | null;
  is_enabled?: boolean;
}

export interface CorrespondenceEmailSettingsPanelHandle {
  save: () => Promise<void>;
  isSaving: () => boolean;
}

export interface CorrespondenceEmailSettingsPanelProps {
  /** Renders without the outer card chrome (for embedding inside a modal). */
  embedded?: boolean;
  /** Hides the internal Save button when a parent provides the action. */
  hideSaveButton?: boolean;
  /** Fired after settings are saved successfully. */
  onSaved?: () => void;
  /** Read-only display (no inputs/save) when the user lacks edit permission. */
  readOnly?: boolean;
}

const initialFormData: CorrespondenceFormData = {
  correspondence_use_external_connector: false,
  correspondence_external_connector_id: '',
  correspondence_external_from_email: '',
  correspondence_external_reply_to_email: '',
};

function TextInput({ label, value, onChange, placeholder, type = 'text', helpText, disabled }: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: string;
  helpText?: string;
  disabled?: boolean;
}) {
  return (
    <div className="space-y-1.5">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{label}</label>
      <input
        type={type}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-gray-900 transition-colors placeholder-gray-400 focus:border-transparent focus:ring-2 focus:ring-blue-500 disabled:opacity-60 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
      />
      {helpText ? <p className="text-xs text-gray-500 dark:text-gray-400">{helpText}</p> : null}
    </div>
  );
}

function SelectInput({ label, value, onChange, options, placeholder, disabled }: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { label: string; value: string }[];
  placeholder?: string;
  disabled?: boolean;
}) {
  return (
    <div className="space-y-1.5">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{label}</label>
      <select
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-gray-900 transition-colors focus:border-transparent focus:ring-2 focus:ring-blue-500 disabled:opacity-60 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
      >
        <option value="">{placeholder || 'Select...'}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function CheckboxInput({ label, checked, onChange, helpText, disabled }: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  helpText?: string;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-start gap-3">
      <input
        type="checkbox"
        checked={checked ?? false}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        className="mt-1 h-5 w-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-60"
      />
      <div>
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</label>
        {helpText ? <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{helpText}</p> : null}
      </div>
    </div>
  );
}

const CorrespondenceEmailSettingsPanel = forwardRef<
  CorrespondenceEmailSettingsPanelHandle,
  CorrespondenceEmailSettingsPanelProps
>(function CorrespondenceEmailSettingsPanel(
  { embedded = false, hideSaveButton = false, onSaved, readOnly = false },
  ref,
) {
  const [formData, setFormData] = useState<CorrespondenceFormData>({ ...initialFormData });
  const [availableConnectors, setAvailableConnectors] = useState<EmailConnector[]>([]);
  const [systemMailboxAddress, setSystemMailboxAddress] = useState('');
  const [connectorsLoading, setConnectorsLoading] = useState(false);
  const [connectorError, setConnectorError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [showConnectorModal, setShowConnectorModal] = useState(false);
  const [savingConnector, setSavingConnector] = useState(false);
  const [connectorForm, setConnectorForm] = useState({
    name: '',
    description: '',
    smart_host: '',
    smart_host_port: 587,
    use_tls: true,
    auth_username: '',
    auth_password: '',
    rewrite_from: '',
  });

  const updateField = useCallback(<K extends keyof CorrespondenceFormData>(field: K, value: CorrespondenceFormData[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setSuccess(false);
  }, []);

  const reloadConnectors = useCallback(async (): Promise<EmailConnector[]> => {
    const tokens = authService.getTokens();
    const connectorsResponse = await fetch('/api/v1/email-domains', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${tokens?.access_token}`,
      },
      body: JSON.stringify({ action: 'getConnectors' }),
    });
    if (!connectorsResponse.ok) {
      const errData = await connectorsResponse.json().catch(() => ({ error: 'Unable to load external connectors' }));
      setConnectorError(errData.error || 'Unable to load external connectors');
      return [];
    }
    const connectorsResult = await connectorsResponse.json();
    if (connectorsResult.success && Array.isArray(connectorsResult.connectors)) {
      const normalized = connectorsResult.connectors
        .filter((connector: EmailConnector) => connector?.id && connector?.smart_host && connector?.auth_username)
        .map((connector: EmailConnector) => ({
          id: connector.id,
          name: connector.name,
          smart_host: connector.smart_host,
          auth_username: connector.auth_username,
          rewrite_from: connector.rewrite_from ?? null,
          is_enabled: connector.is_enabled,
        }));
      setAvailableConnectors(normalized);
      return normalized;
    }
    setConnectorError(connectorsResult.error || 'Unable to load external connectors');
    return [];
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      setConnectorsLoading(true);
      try {
        const tokens = authService.getTokens();
        if (!tokens?.access_token) {
          throw new Error('No authentication token found');
        }

        const [settingsResponse] = await Promise.all([
          fetch('/api/v1/client/settings', {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${tokens.access_token}`,
            },
          }),
          reloadConnectors(),
        ]);

        if (!settingsResponse.ok) {
          const errorData = await settingsResponse.json().catch(() => ({ error: 'Failed to load correspondence settings' }));
          throw new Error(errorData.error || 'Failed to load correspondence settings');
        }

        const settingsResult = await settingsResponse.json();
        if (settingsResult.success && settingsResult.data) {
          const data = settingsResult.data;
          setSystemMailboxAddress(data.email_totalhub ?? '');
          setFormData({
            correspondence_use_external_connector: data.correspondence_use_external_connector ?? false,
            correspondence_external_connector_id: data.correspondence_external_connector_id ? String(data.correspondence_external_connector_id) : '',
            correspondence_external_from_email: data.correspondence_external_from_email ?? '',
            correspondence_external_reply_to_email: data.correspondence_external_reply_to_email ?? '',
          });
        }
      } catch (err: unknown) {
        console.error('[CorrespondenceEmailSettingsPanel] Load failed:', err);
        setError(err instanceof Error ? err.message : 'Failed to load correspondence settings');
      } finally {
        setConnectorsLoading(false);
        setLoading(false);
      }
    };

    void load();
  }, [reloadConnectors]);

  const selectedConnector = useMemo(
    () => availableConnectors.find((connector) => String(connector.id) === formData.correspondence_external_connector_id),
    [availableConnectors, formData.correspondence_external_connector_id],
  );

  const handleSubmit = useCallback(async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      const tokens = authService.getTokens();
      if (!tokens?.access_token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch('/api/v1/client/settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${tokens.access_token}`,
        },
        body: JSON.stringify({
          correspondence_use_external_connector: formData.correspondence_use_external_connector,
          correspondence_external_connector_id: formData.correspondence_external_connector_id ? Number(formData.correspondence_external_connector_id) : null,
          correspondence_external_from_email: formData.correspondence_external_from_email || null,
          correspondence_external_reply_to_email: formData.correspondence_external_reply_to_email || null,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to update correspondence settings' }));
        throw new Error(errorData.error || 'Failed to update correspondence settings');
      }

      setSuccess(true);
      window.setTimeout(() => setSuccess(false), 5000);
      onSaved?.();
    } catch (err: unknown) {
      console.error('[CorrespondenceEmailSettingsPanel] Update failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to update correspondence settings');
    } finally {
      setSaving(false);
    }
  }, [formData, onSaved]);

  useImperativeHandle(ref, () => ({
    save: handleSubmit,
    isSaving: () => saving,
  }), [handleSubmit, saving]);

  const handleCreateConnector = async () => {
    if (!connectorForm.name.trim()) {
      notify.error('Connector name is required');
      return;
    }
    if (!connectorForm.smart_host.trim()) {
      notify.error('SMTP server is required');
      return;
    }
    if (!connectorForm.auth_username.trim()) {
      notify.error('Username is required');
      return;
    }

    try {
      setSavingConnector(true);
      const tokens = authService.getTokens();
      if (!tokens?.access_token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch('/api/v1/email-domains', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${tokens.access_token}`,
        },
        body: JSON.stringify({
          action: 'createConnector',
          name: connectorForm.name,
          description: connectorForm.description || null,
          connector_type: 'smtp_relay',
          direction: 'outbound',
          is_enabled: true,
          smart_host: connectorForm.smart_host,
          smart_host_port: connectorForm.smart_host_port,
          use_tls: connectorForm.use_tls,
          auth_username: connectorForm.auth_username,
          auth_password: connectorForm.auth_password || null,
          rewrite_from: connectorForm.rewrite_from || null,
          priority: 100,
        }),
      });

      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to create connector');
      }

      notify.success('Connector created successfully');
      setShowConnectorModal(false);
      setConnectorForm({
        name: '',
        description: '',
        smart_host: '',
        smart_host_port: 587,
        use_tls: true,
        auth_username: '',
        auth_password: '',
        rewrite_from: '',
      });

      await reloadConnectors();

      if (result.connector?.id && !formData.correspondence_external_connector_id) {
        updateField('correspondence_external_connector_id', String(result.connector.id));
        updateField('correspondence_external_from_email', result.connector.rewrite_from || result.connector.auth_username || '');
      }
    } catch (err: unknown) {
      console.error('[CorrespondenceEmailSettingsPanel] Create connector failed:', err);
      notify.error(err instanceof Error ? err.message : 'Failed to create connector');
    } finally {
      setSavingConnector(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center space-y-4 py-16">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <p className="text-sm text-gray-600 dark:text-gray-400">Loading correspondence settings...</p>
      </div>
    );
  }

  return (
    <div className={embedded ? 'space-y-6' : 'space-y-6 rounded-xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800'}>
      {success ? (
        <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-800 dark:bg-green-900/20">
          <Check className="h-5 w-5 text-green-600 dark:text-green-400" />
          <p className="text-sm text-green-800 dark:text-green-200">Correspondence settings saved successfully!</p>
        </div>
      ) : null}

      {error ? (
        <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
          <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
          <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
        </div>
      ) : null}

      <div className="rounded-2xl border border-blue-200 bg-blue-50/80 p-5 dark:border-blue-900/60 dark:bg-blue-950/20">
        <div className="flex items-start gap-3">
          <div className="rounded-xl bg-blue-100 p-2 dark:bg-blue-900/40">
            <Mail className="h-5 w-5 text-blue-600 dark:text-blue-300" />
          </div>
          <div className="space-y-2">
            <h4 className="text-base font-semibold text-gray-900 dark:text-white">
              Outbound correspondence routing
              <UserDocsLink
                docsPath="/total-operations/workflows/email-task-management/sources-automations#correspondence-connector"
                label="User guide: Correspondence connector"
                className="ml-1 align-middle"
              />
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Campaign emails, finance documents, reports, and similar correspondence will use the selected connector when enabled.
              If you leave this off, TotalAccess falls back to <span className="font-medium">no-reply@totalaccess.co.za</span>.
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Replies can still land in your TotalHub mailbox: <span className="font-medium">{systemMailboxAddress || 'Not provisioned yet'}</span>
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-4 rounded-xl border border-gray-200 bg-white p-5 dark:border-gray-700 dark:bg-gray-900/20">
        <CheckboxInput
          label="Send client correspondence through an external connector"
          checked={formData.correspondence_use_external_connector}
          onChange={(checked) => updateField('correspondence_use_external_connector', checked)}
          helpText="When enabled, the selected connector becomes the default sender for operational and finance correspondence."
          disabled={readOnly}
        />

        {formData.correspondence_use_external_connector ? (
          <div className="space-y-4 rounded-xl border border-dashed border-gray-300 p-4 dark:border-gray-700">
            {connectorsLoading ? (
              <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading available connectors...
              </div>
            ) : availableConnectors.length === 0 ? (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200">
                No external connectors are available yet. Create one below to send through your own mail server.
              </div>
            ) : (
              <>
                <SelectInput
                  label="External Connector"
                  value={formData.correspondence_external_connector_id}
                  onChange={(value) => {
                    const connector = availableConnectors.find((item) => String(item.id) === value);
                    updateField('correspondence_external_connector_id', value);
                    updateField('correspondence_external_from_email', connector?.rewrite_from || connector?.auth_username || '');
                  }}
                  options={availableConnectors.map((connector) => ({
                    value: String(connector.id),
                    label: `${connector.name} — ${connector.rewrite_from || connector.auth_username}`,
                  }))}
                  placeholder="Select an external connector"
                  disabled={readOnly}
                />

                {selectedConnector ? (
                  <div className="space-y-4">
                    <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800/70">
                      <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Sender address</p>
                      <p className="mt-1 text-sm font-semibold text-gray-900 dark:text-white">
                        {selectedConnector.rewrite_from || selectedConnector.auth_username}
                      </p>
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">This becomes the visible sender for client correspondence.</p>
                    </div>
                    <TextInput
                      label="Reply-to email"
                      value={formData.correspondence_external_reply_to_email}
                      onChange={(value) => updateField('correspondence_external_reply_to_email', value)}
                      type="email"
                      placeholder="replies@yourcompany.com"
                      helpText="Optional: specify where replies should be sent. If left blank, replies will go to the TotalHub mailbox."
                      disabled={readOnly}
                    />
                  </div>
                ) : formData.correspondence_external_connector_id ? (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200">
                    The saved connector is no longer available. Please choose another connector before saving.
                  </div>
                ) : null}
              </>
            )}

            {connectorError ? (
              <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300">
                {connectorError}
              </div>
            ) : null}

            {!readOnly && (
              <div className="border-t border-gray-200 pt-2 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => setShowConnectorModal(true)}
                  disabled={savingConnector}
                  className="inline-flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-medium text-blue-700 transition-colors hover:bg-blue-100 disabled:opacity-50 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-300 dark:hover:bg-blue-900/30"
                >
                  <Plus className="h-4 w-4" />
                  Add New Connector
                </button>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Create a new SMTP connector for sending outbound correspondence.
                </p>
              </div>
            )}
          </div>
        ) : null}
      </div>

      {!readOnly && !hideSaveButton && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            {saving ? 'Saving...' : 'Save Email Settings'}
          </button>
        </div>
      )}

      {showConnectorModal ? (
        <div className="fixed inset-0 z-[220] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white shadow-2xl dark:bg-gray-800">
            <div className="flex items-center justify-between border-b border-gray-200 p-6 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className="rounded-xl bg-blue-100 p-2 dark:bg-blue-900/30">
                  <Server className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Add Email Connector</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Configure an SMTP relay for outbound correspondence</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowConnectorModal(false)}
                className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-300"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4 p-6">
              <TextInput
                label="Connector Name *"
                value={connectorForm.name}
                onChange={(value) => setConnectorForm((prev) => ({ ...prev, name: value }))}
                placeholder="e.g., Primary SMTP Relay"
              />

              <TextInput
                label="Description"
                value={connectorForm.description}
                onChange={(value) => setConnectorForm((prev) => ({ ...prev, description: value }))}
                placeholder="Optional description"
              />

              <div className="grid grid-cols-2 gap-4">
                <TextInput
                  label="SMTP Server *"
                  value={connectorForm.smart_host}
                  onChange={(value) => setConnectorForm((prev) => ({ ...prev, smart_host: value }))}
                  placeholder="smtp.example.com"
                />

                <SelectInput
                  label="Port"
                  value={String(connectorForm.smart_host_port)}
                  onChange={(value) => setConnectorForm((prev) => ({ ...prev, smart_host_port: parseInt(value, 10) }))}
                  options={[
                    { value: '25', label: '25 (SMTP)' },
                    { value: '465', label: '465 (SMTPS)' },
                    { value: '587', label: '587 (Submission)' },
                    { value: '2525', label: '2525 (Alternative)' },
                  ]}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <TextInput
                  label="Username *"
                  value={connectorForm.auth_username}
                  onChange={(value) => setConnectorForm((prev) => ({ ...prev, auth_username: value }))}
                  placeholder="you@yourcompany.com"
                />

                <TextInput
                  label="Password"
                  value={connectorForm.auth_password}
                  onChange={(value) => setConnectorForm((prev) => ({ ...prev, auth_password: value }))}
                  type="password"
                  placeholder="SMTP password"
                />
              </div>

              <TextInput
                label="Send-As Address"
                value={connectorForm.rewrite_from}
                onChange={(value) => setConnectorForm((prev) => ({ ...prev, rewrite_from: value }))}
                type="email"
                placeholder="campaigns@yourcompany.com"
                helpText="The visible sender address for outbound correspondence."
              />

              <CheckboxInput
                label="Use TLS encryption"
                checked={connectorForm.use_tls}
                onChange={(checked) => setConnectorForm((prev) => ({ ...prev, use_tls: checked }))}
                helpText="Recommended. Disable only for legacy servers."
              />
            </div>

            <div className="flex items-center justify-end gap-3 border-t border-gray-200 p-6 dark:border-gray-700">
              <button
                type="button"
                onClick={() => setShowConnectorModal(false)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void handleCreateConnector()}
                disabled={savingConnector}
                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {savingConnector ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                {savingConnector ? 'Creating...' : 'Create Connector'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
});

export default CorrespondenceEmailSettingsPanel;
